import React, { useEffect, useState } from "react"
import { FaLock, FaLockOpen } from "react-icons/fa"
import { useDispatch, useSelector } from "react-redux"
import { setLockStatus, setTriedToLock } from "../../../features/schedule/scheduleSlice"
import { usePostHog } from "posthog-js/react"
import { useAuth0 } from "@auth0/auth0-react"
import {
	getDateFormatted,
	getFirstOpenDateToday,
	getFirstOpenDateTomorrow,
	getCurrentTime,
	getClinicTimes
} from "../../../utils/generalUtils"
import { handleSubmit } from "../NurseTable"
import Tooltip from "../../Tooltip"
import useLogging from "../../../hooks/useLogging"
import useFetchSettings from "../../../hooks/useFetchSettings"

const SubmitScheduleButton = ({
	isLocked,
	hasWriteSchedulesPermission,
	setNotification,
	isTomorrowMode,
	setBtnSelect,
	tableNurses,
	setEditsMade,
	selectedNurseName,
	selectedRole,
	selectedShiftStart,
	selectedShiftEnd
}) => {
	const dispatch = useDispatch()
	const posthog = usePostHog()
	const sandbox = useSelector(state => state.sandbox.isSandboxMode)
	const logging = useLogging()

	const { user, getAccessTokenSilently } = useAuth0()

	const { settingsData } = useFetchSettings()

	const testEnvironment = user.org_id === "org_lkg3WMhi3Q2l92iT"
	const centerOpen = settingsData?.centerOpen
	const centerClosedDates = settingsData?.center_closed_dates || []

	const nextDate = getDateFormatted(
		getFirstOpenDateTomorrow(centerOpen, testEnvironment, centerClosedDates)
	)

	// Calculate open time for today using the new date-aware method
	const todayDate = getFirstOpenDateToday(centerOpen, testEnvironment, centerClosedDates)

	const { openTime } = getClinicTimes(settingsData, todayDate)

	const today = getDateFormatted(todayDate)

	const currentTime = getCurrentTime(sandbox, isTomorrowMode)

	// Get tomorrowNurses from Redux store
	const triedToLock = useSelector(state => state.schedule.triedToLock)

	const handleLockSchedule = async () => {
		posthog.capture("lock-schedule")
		logging.info("handleLockSchedule", { labels: { sandbox, isTomorrowMode, isLocked } })

		if (isTomorrowMode || sandbox || isLocked) {
			logging.warn(
				"Cannot lock schedule in tomorrow mode, sandbox mode, or if schedule is already locked"
			)
			return
		}

		const success = await submitTomorrowNurses()

		if (!success) {
			return
		}

		try {
			const token = await getAccessTokenSilently({
				audience: process.env.REACT_APP_API_IDENTIFIER
			})

			const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lock-schedule`, {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
				body: JSON.stringify({ date: today })
			})

			if (!response.ok) {
				const errorData = await response.json()
				logging.warn("Failed to lock schedule:", {
					error: errorData.message,
					stack: errorData.stack
				})
				setNotification({
					visible: true,
					message: "Failed to lock schedule",
					success: false
				})
				setTimeout(() => {
					setNotification(prev => ({ ...prev, visible: false }))
				}, 2000)
			} else {
				await response.json()
				logging.info("Schedule locked successfully")
				// Update all state at once before navigation
				dispatch(setLockStatus(true))
				dispatch(setTriedToLock(true))
				setNotification({
					visible: true,
					message: "Schedule locked successfully",
					success: true
				})
			}
		} catch (error) {
			logging.error("Error locking schedule:", { error: error.message, stack: error.stack })
			setNotification({ visible: true, message: "Error locking schedule", success: false })
			setTimeout(() => {
				setNotification(prev => ({ ...prev, visible: false }))
			}, 2000)
		}
	}

	const handleUndoLock = async () => {
		posthog.capture("unlock-schedule")
		if (currentTime >= openTime) {
			setNotification({
				visible: true,
				message: "Cannot unsubmit schedule after infusion center has opened!",
				success: false
			})
			setTimeout(() => {
				setNotification({ visible: false, message: "" })
			}, 3000)
			return
		}

		dispatch(setLockStatus(false)) // Optimistically update the state

		try {
			const token = await getAccessTokenSilently({
				audience: process.env.REACT_APP_API_IDENTIFIER
			})

			const response = await fetch(
				`${process.env.REACT_APP_BACKEND_URL}/api/unlock-schedule`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}` // Add authorization header
					},
					body: JSON.stringify({ date: today }) // Use today's date
				}
			)

			if (!response.ok) {
				const errorData = await response.json()
				logging.error("Failed to unlock schedule:", {
					error: errorData.message,
					stack: errorData.stack
				})
				alert("Failed to unlock schedule: " + errorData.message)
				dispatch(setLockStatus(true)) // Revert state if unlocking fails
			} else {
				await response.json()
				logging.info("Schedule unlocked successfully")
			}
		} catch (error) {
			logging.error("Error unlocking schedule:", { error: error.message, stack: error.stack })
			alert("Error unlocking schedule")
			dispatch(setLockStatus(true)) // Revert state on error
		}
	}

	const submitTomorrowNurses = async () => {
		// Check if nurse staffing is within valid range

		const success = await handleSubmit(
			tableNurses,
			selectedNurseName,
			selectedRole,
			selectedShiftStart,
			selectedShiftEnd,
			setNotification,
			setEditsMade,
			nextDate,
			getAccessTokenSilently,
			centerOpen,
			testEnvironment,
			centerClosedDates,
			logging
		)

		if (!success) {
			setBtnSelect("Future Staffing")
			logging.warn("Unable to submit tomorrow's schedule so moved to future staffing page")
			return false
		}

		return true
	}

	useEffect(() => {
		const checkAndLockSchedule = async () => {
			try {
				if (!openTime) {
					logging.warn("Open time is not defined.")
					return
				}
				if (
					currentTime >= openTime &&
					!isLocked &&
					!sandbox &&
					!isTomorrowMode &&
					!triedToLock &&
					centerOpen
				) {
					handleLockSchedule()
				}
			} catch (error) {
				logging.warn("Error in checkAndLockSchedule:", {
					error: error.message,
					stack: error.stack
				})
			}
		}

		checkAndLockSchedule() // Immediately run the check

		const interval = setInterval(checkAndLockSchedule, 900000) // Run every 15 minutes

		return () => clearInterval(interval) // Clean up on unmount
		// `handleLockSchedule` is stable within this component context; `logging` is a fresh object each render (useLogging returns inline object); adding them would re-run every interval.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [openTime, currentTime, isLocked, sandbox, isTomorrowMode, centerOpen, triedToLock])

	const [isHovered, setIsHovered] = useState(false)
	const isEnabled = !sandbox && !isTomorrowMode

	return (
		<div className="flex flex-wrap items-center space-x-3">
			<div className="relative">
				<>
					{hasWriteSchedulesPermission && (
						<>
							{isLocked ? (
								<button
									onClick={handleUndoLock}
									className={`px-4 py-2 rounded-md flex items-center ${
										isEnabled
											? "text-white bg-yellow-500 hover:bg-yellow-700"
											: "text-black bg-gray-100 hover:bg-gray-200"
									} ${!isEnabled && "opacity-50 cursor-not-allowed"}`}
									onMouseEnter={() => setIsHovered(true)}
									onMouseLeave={() => setIsHovered(false)}
								>
									<FaLockOpen className="mr-2" />
									Unsubmit Schedule
								</button>
							) : (
								<>
									{/* Only show the lock button if not in tomorrow mode */}
									<button
										onClick={handleLockSchedule}
										className={`px-4 py-2 rounded-md flex items-center ${
											isEnabled
												? "text-white bg-green-500 hover:bg-green-700"
												: "text-black bg-gray-100 hover:bg-gray-200"
										} ${!isEnabled && "opacity-50 cursor-not-allowed"}`}
										onMouseEnter={() => setIsHovered(true)}
										onMouseLeave={() => setIsHovered(false)}
									>
										<FaLock className="mr-2" />
										Submit Schedule
									</button>
								</>
							)}
						</>
					)}
				</>

				{!isEnabled && isHovered && (
					<Tooltip content="Schedule can only be submitted in today mode">
						<div className="fixed mt-1 w-64 bg-gray-800 text-white text-sm rounded-md py-2 px-4 shadow-lg z-50">
							Schedule can only be submitted in today mode
						</div>
					</Tooltip>
				)}
			</div>
		</div>
	)
}

export default SubmitScheduleButton
